import { cn } from '../cn';
import Button from '../Input2/Button';
import { General } from '../StrokeIcons';
import StepIconSvg from '../StepIcon/StepIconSvg';
import type { CadenceStepTypePure } from '../CGStep/types';
import { getBlockStepItemsWithStatus } from './blockStepRules';

export type CampaignStepPickerPureProps = {
  /** Step types already present in the day block — used to disable once-only types. */
  existingTypes: readonly CadenceStepTypePure[];
  /**
   * Extra per-type disabled reasons supplied by the host (e.g. "You are
   * already connected to this contact." for `connect`). When set, the
   * matching type renders disabled with this reason as the tooltip.
   */
  disabledTypeReasons?: Partial<Record<CadenceStepTypePure, string>>;
  onPickType: (type: CadenceStepTypePure) => void;
  onCancel?: () => void;
  className?: string;
  /**
   * Size for the step-type option buttons. Defaults to `'md'`. Hosts in
   * narrower layouts (e.g. the wait-for-connect No-branch pink well)
   * pass `'sm'` so the buttons fit without wrapping.
   */
  optionSize?: 'sm' | 'md';
  /**
   * When true, the host day card is rendering in the purple
   * "Not in template" tint. Each option button picks up a purple hover
   * so the picker reads as part of the same tinted surface instead of
   * surfacing a generic gray hover.
   */
  isDifferentFromTemplate?: boolean;
  /** When true, voice_note step type is available in the picker. */
  voiceEnabled?: boolean;
  /** When true, the `call` step type is available in the picker (team voice-calling flag). */
  voiceCallingEnabled?: boolean;
};

const TYPE_DESCRIPTIONS: Record<string, string> = {
  connect: 'LinkedIn connection request',
  message: 'LinkedIn message',
  inmail: 'LinkedIn InMail',
  likeRecentPosts: 'Like a recent post',
  follow: 'Follow profile',
  email: 'Send an email',
  reminder: 'Manual task reminder',
  call: 'Phone call',
  voice_note: 'Send a voice note',
};

export function CampaignStepPickerPure({
  existingTypes,
  disabledTypeReasons,
  onPickType,
  onCancel,
  className,
  optionSize = 'md',
  isDifferentFromTemplate,
  voiceEnabled,
  voiceCallingEnabled,
}: CampaignStepPickerPureProps) {
  const items = getBlockStepItemsWithStatus(existingTypes, disabledTypeReasons).filter(
    ({ item }) =>
      (voiceEnabled || item.value !== 'voice_note') && (voiceCallingEnabled || item.value !== 'call'),
  );

  return (
    <div
      data-testid="campaign-step-picker"
      className={cn(
        // No top border / explicit bg here — the host card (saved day card
        // OR draft new-day card) already provides the chrome. Adding a
        // border here produced a double-line above the picker when nested
        // inside the dashed draft card. Slim padding so all 8 step icons
        // fit on one row in narrow contexts (e.g. wait-for-connect No
        // branch) without wrapping.
        'flex w-full flex-col px-2 py-3',
        className,
      )}>
      <div className="mb-2 flex items-center justify-between">
        <span
          className="text-xs font-semibold uppercase tracking-wide text-lynch-700 dark:text-lynch-300"
          data-testid="campaign-step-picker-heading">
          Add a step
        </span>
        {onCancel != null && (
          <Button
            data-testid="campaign-step-picker-cancel"
            icon={<General.XCircle />}
            size="xs"
            text
            color="gray"
            onClick={onCancel}>
            cancel
          </Button>
        )}
      </div>
      {items.length === 0 && (
        <p
          className="px-1 py-2 text-xs italic text-lynch-600 dark:text-lynch-400"
          data-testid="campaign-step-picker-empty">
          No more step types can be added to this day.
        </p>
      )}
      {items.length > 0 && (
        <div
          className="flex flex-wrap gap-1"
          role="group"
          aria-label="Step types"
          data-testid="campaign-step-picker-options">
          {items.map(({ item, disabled, disabledReason }) => {
            const description = TYPE_DESCRIPTIONS[item.value];
            return (
              <Button
                key={item.value}
                data-testid={`campaign-step-picker-option-${item.value}`}
                data-step-type={item.value}
                data-disabled-reason={disabled ? 'once-only-already-added' : undefined}
                aria-label={item.label}
                aria-disabled={disabled || undefined}
                // readOnly (not disabled) so the Tooltip still fires on hover.
                // Per CLAUDE.md: disabled buttons swallow pointer events and
                // break tooltips; readOnly renders as a <span> with hover ok.
                readOnly={disabled}
                className={cn(
                  disabled && 'cursor-not-allowed opacity-50',
                  // Override the `default` variant's gray hover with a
                  // purple hover when the host day card is purple-tinted.
                  isDifferentFromTemplate &&
                    'hover:bg-purple-100 hover:text-purple-700 dark:hover:bg-purple-950/50 dark:hover:text-purple-200',
                )}
                tooltip={
                  <span>
                    <span className="font-semibold">{item.label}</span>
                    {description && (
                      <>
                        <br />
                        <span className="text-xs opacity-80">{description}</span>
                      </>
                    )}
                    {disabled && disabledReason && (
                      <>
                        <br />
                        <span className="mt-1 inline-block text-xs italic text-amber-200 dark:text-yellow-800">
                          {disabledReason}
                        </span>
                      </>
                    )}
                  </span>
                }
                tooltipPosition="top"
                icon={<StepIconSvg iconType={item.value} />}
                size={optionSize}
                color="gray"
                onClick={() => {
                  if (!disabled) onPickType(item.value);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
